/**
 * Display bucketing, shared by every section of the Statistics tab.
 *
 * The backend always writes per-minute buckets. A 7-day range therefore holds 10 080 of them,
 * which is far more than a chart can draw, so buckets are folded into coarser display buckets whose
 * size follows the range.
 *
 * These four helpers previously existed twice — in the egresses dashboard store and again inside
 * Jobs' `StatisticsTab.vue` — and the copies had already drifted, so the same range rendered
 * different axes in different views.
 *
 * Pure functions over a range, deliberately kept out of the view contexts that call them: that is
 * what makes them testable without constructing a context.
 */

import type { DateRange } from './traces-view-context'


export function calculateBucketMinutes(rangeMs: number): number {
  const hours = rangeMs / (1000 * 60 * 60)
  if (hours <= 2) return 1
  if (hours <= 6) return 5
  if (hours <= 24) return 15
  if (hours <= 72) return 60
  if (hours <= 168) return 360
  return 1440
}

export function roundToBucket(date: Date, bucketMinutes: number): Date {
  const result = new Date(date)
  result.setSeconds(0, 0)

  if (bucketMinutes >= 1440) {
    result.setHours(0, 0, 0, 0)
  } else if (bucketMinutes >= 60) {
    const hours = Math.floor(bucketMinutes / 60)
    result.setMinutes(0)
    result.setHours(Math.floor(result.getHours() / hours) * hours)
  } else {
    result.setMinutes(Math.floor(result.getMinutes() / bucketMinutes) * bucketMinutes)
  }

  return result
}

/** Every display bucket in the range, oldest first, as ISO strings. */
export function bucketTimes(range: DateRange, bucketMinutes: number): string[] {
  const times: string[] = []
  const current = roundToBucket(range.from, bucketMinutes)

  while (current <= range.to) {
    times.push(current.toISOString())
    current.setMinutes(current.getMinutes() + bucketMinutes)
  }

  return times
}

/**
 * Axis labels, one per display bucket.
 *
 * Sub-day buckets carry the date as well as the time. `LineChart` matches a series' points to the
 * axis by label, so a duplicated label would silently collapse two buckets into one — which is
 * exactly what a bare time does on any range spanning midnight.
 */
export function bucketLabels(times: string[], bucketMinutes: number): string[] {
  return times.map((iso) => {
    const date = new Date(iso)

    if (bucketMinutes >= 1440) {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
    }

    const day = date.toLocaleDateString([], { month: 'short', day: 'numeric' })
    const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    return `${day} ${time}`
  })
}

/**
 * Folds per-minute rows into display buckets keyed by label.
 *
 * `combine` receives every raw bucket falling inside one display bucket, and returns the value for
 * it — or null when there is nothing to report. Null matters: `LineChart` draws it as a gap, and a
 * minute with no calls has no latency rather than zero latency.
 */
export function foldSeries<T extends { bucketTime: Date }>(
  rows: T[],
  times: string[],
  labels: string[],
  bucketMinutes: number,
  combine: (rows: T[]) => number | null,
): { label: string; value: number | null }[] {
  const byDisplayBucket = new Map<string, T[]>()

  for (const row of rows) {
    const key = roundToBucket(new Date(row.bucketTime), bucketMinutes).toISOString()
    const existing = byDisplayBucket.get(key)
    if (existing) {
      existing.push(row)
    } else {
      byDisplayBucket.set(key, [row])
    }
  }

  return times.map((iso, index) => ({
    label: labels[index],
    value: combine(byDisplayBucket.get(iso) ?? []),
  }))
}

/** Sums a counter across a display bucket. Absence of rows is zero traffic, which is a real zero. */
export function sumOf<T>(rows: T[], select: (row: T) => number | null | undefined): number {
  return rows.reduce((total, row) => total + (select(row) ?? 0), 0)
}

/**
 * Volume-weighted mean, or null when nothing was measured.
 *
 * Weighted rather than a mean of means: ten calls in one minute and one in the next must not
 * contribute equally to the average latency of the two.
 */
export function weightedAverage<T>(
  rows: T[],
  value: (row: T) => number | null | undefined,
  weight: (row: T) => number | null | undefined,
): number | null {
  let sum = 0
  let total = 0

  for (const row of rows) {
    const v = value(row)
    const w = weight(row) ?? 0
    if (v != null && w > 0) {
      sum += v * w
      total += w
    }
  }

  return total > 0 ? sum / total : null
}

/** The extreme across a display bucket, or null when no row reported one. */
export function extremeOf<T>(
  rows: T[],
  select: (row: T) => number | null | undefined,
  pick: (a: number, b: number) => number,
): number | null {
  let result: number | null = null

  for (const row of rows) {
    const v = select(row)
    if (v != null) {
      result = result == null ? v : pick(result, v)
    }
  }

  return result
}

/** Highest gauge reading in a display bucket — a peak, since gauges are not additive. */
export function peakOf<T>(rows: T[], select: (row: T) => number | null | undefined): number | null {
  return extremeOf(rows, select, Math.max)
}
